import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Lock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const VideoQuizPlayer = ({ src, moduleId, authHeader = {}, onCompleted, onInterrupt }) => {
  const videoRef = useRef(null);
  const usedTimes = useRef(new Set());
  const queueRef = useRef([]);
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answered, setAnswered] = useState(null);
  const [result, setResult] = useState(null);
  const [times, setTimes] = useState([]);
  const [warn, setWarn] = useState(false);
  const [done, setDone] = useState(null);
  const [endMode, setEndMode] = useState(false);
  const [announce, setAnnounce] = useState(false);
  const [closing, setClosing] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const announceTimer = useRef(null);

  const rampVolume = (v) => {
    v.volume = 0;
    let x = 0;
    const iv = setInterval(() => {
      x += 0.08;
      v.volume = Math.min(1, x);
      if (x >= 1) clearInterval(iv);
    }, 80);
  };

  useEffect(() => {
    usedTimes.current = new Set();
    queueRef.current = [];
    setQuiz(null); setDone(null); setEndMode(false);
    fetch(src.replace('.mp4', '.quiztimes.json'))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.quiz_times)) setTimes(d.quiz_times); })
      .catch(() => {});
    if (moduleId) {
      axios.get(`${API}/api/video-quiz/${moduleId}`, authHeader)
        .then((r) => {
          const open = r.data.questions.filter((q) => !r.data.correct_ids.includes(q.id));
          queueRef.current = open;
          setOpenCount(open.length);
          if (r.data.completed) setDone({ already: true });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, moduleId]);

  const exitFullscreen = () => {
    const v = videoRef.current;
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      if (v && v.webkitDisplayingFullscreen) v.webkitExitFullscreen();
    } catch (e) {}
  };

  const openNextQuestion = () => {
    const q = queueRef.current.shift();
    if (!q) return false;
    exitFullscreen();
    onInterrupt?.();
    setAnswered(null); setResult(null); setWarn(false);
    setAnnounce(true);
    try { new Audio('/sounds/quizfrage.mp3').play().catch(() => {}); } catch (e) {}
    clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => setAnnounce(false), 2600);
    setQuiz(q);
    return true;
  };
  useEffect(() => () => clearTimeout(announceTimer.current), []);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || quiz || endMode) return;
    const t = times.find((ct) => !usedTimes.current.has(ct) && v.currentTime >= ct - 0.45 && v.currentTime < ct + 2.5);
    if (t !== undefined && queueRef.current.length > 0) {
      usedTimes.current.add(t);
      let vol = v.volume;
      const iv = setInterval(() => {
        vol -= 0.34;
        if (vol <= 0) {
          clearInterval(iv);
          v.pause();
          v.volume = 1;
          openNextQuestion();
        } else {
          v.volume = Math.max(0, vol);
        }
      }, 50);
      return;
    }
    const next = times.find((ct) => !usedTimes.current.has(ct) && v.currentTime < ct);
    setWarn(next !== undefined && queueRef.current.length > 0 && v.currentTime >= next - 6);
  };

  const onEnded = () => {
    if (queueRef.current.length > 0) {
      setEndMode(true);
      openNextQuestion();
    } else if (!done && moduleId) {
      axios.get(`${API}/api/video-quiz/${moduleId}`, authHeader)
        .then((r) => {
          if (r.data.completed) { setDone({ already: true }); return; }
          const open = r.data.questions.filter((q) => !r.data.correct_ids.includes(q.id));
          if (open.length > 0) {
            queueRef.current = open;
            setOpenCount(open.length);
            setEndMode(true);
            openNextQuestion();
          }
        })
        .catch(() => {});
    }
  };

  const answer = async (i) => {
    if (answered !== null || !quiz) return;
    setAnswered(i);
    try {
      const r = await axios.post(`${API}/api/video-quiz/${moduleId}/answer`,
        { question_id: quiz.id, answer_index: i }, authHeader);
      setResult(r.data);
      if (!r.data.correct) queueRef.current.push(quiz);
      setOpenCount(queueRef.current.length);
      if (r.data.completed) onCompleted?.();
    } catch (e) {
      setResult({ correct: null, explanation: 'Antwort konnte nicht gespeichert werden — bitte erneut versuchen.' });
      setAnswered(null);
    }
  };

  const resume = () => {
    const completedNow = result?.completed ? result : null;
    if (completedNow) {
      setQuiz(null); setAnswered(null); setResult(null);
      setDone(completedNow);
      return;
    }
    if (endMode) {
      setQuiz(null); setAnswered(null); setResult(null);
      if (!openNextQuestion()) setEndMode(false);
      return;
    }
    setClosing(true);
    const v = videoRef.current;
    if (v) {
      v.play();
      rampVolume(v);
    }
    setTimeout(() => {
      setQuiz(null); setAnswered(null); setResult(null); setClosing(false);
    }, 600);
  };

  return (
    <div className="relative" data-testid="video-quiz-player">
      <style>{`@keyframes vqFade { from { opacity: 0; } to { opacity: 1; } } @keyframes vqAnnounceIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <video ref={videoRef} controls className="w-full" preload="metadata" src={src}
        onTimeUpdate={onTimeUpdate} onEnded={onEnded} onPlay={() => onInterrupt?.()} data-testid="lernvideo-video" />
      {openCount > 0 && !done && (
        <div className="flex items-start gap-2 px-4 py-2.5 text-xs sm:text-sm" style={{ background: '#F6EFE4', color: '#544838', borderTop: '2px solid #D0B994' }} data-testid="video-quiz-pending-hint">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#9E733C' }} />
          <span>
            In diesem Video {openCount === 1 ? 'wartet noch 1 Verständnisfrage' : `warten ${openCount} Verständnisfragen`} auf dich.
            Erst wenn alle richtig beantwortet sind, ist Modul 1 abgeschlossen und Modul 2 wird freigeschaltet.
          </span>
        </div>
      )}
      {warn && !quiz && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs sm:text-sm shadow-md pointer-events-none"
          style={{ background: 'rgba(246,239,228,0.95)', color: '#9E733C', border: '1px solid #D0B994' }}
          data-testid="video-quiz-warning">
          Gleich: kurze Verständnisfrage
        </div>
      )}
      {quiz && announce && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6" style={{ background: '#F6EFE4', animation: 'vqFade 0.5s ease both' }} data-testid="video-quiz-announce">
          <div className="text-center">
            <div className="text-[11px] sm:text-xs mb-3" style={{ color: '#9E733C', letterSpacing: '0.3em' }}>D I C K I E S&nbsp;&nbsp;C A M P U S</div>
            <p className="font-display font-light text-3xl sm:text-5xl" style={{ color: '#262016', animation: 'vqAnnounceIn 0.6s ease 0.2s both' }}>
              Eine Frage an dich
            </p>
            <div className="h-[2px] w-12 mx-auto mt-5" style={{ background: '#9E733C' }} />
          </div>
        </div>, document.body)}
      {quiz && !announce && createPortal(
        <div className={`fixed inset-0 z-[70] overflow-y-auto p-5 sm:p-10 transition-opacity duration-500 ${closing ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          style={{ background: '#F6EFE4', animation: 'vqFade 0.4s ease both' }} data-testid="video-quiz-overlay">
          <div className="max-w-2xl w-full mx-auto min-h-full flex flex-col">
            <div className="my-auto py-4">
              <div className="flex items-center gap-2 text-[11px] sm:text-xs mb-2" style={{ color: '#9E733C', letterSpacing: '0.3em' }}>
                D I C K I E S&nbsp;&nbsp;C A M P U S
              </div>
              <div className="h-[2px] w-10 mb-4 sm:mb-6" style={{ background: '#D0B994' }} />
              <p className={`font-display font-light leading-snug mb-5 sm:mb-7 ${quiz.question.length > 220 ? 'text-base sm:text-xl' : 'text-xl sm:text-3xl'}`} style={{ color: '#262016' }} data-testid="video-quiz-question">
                {quiz.question}
              </p>
              <div className="space-y-2 sm:space-y-3">
                {quiz.options.map((opt, i) => {
                  const isCorrect = result && i === result.correct_index;
                  const chosen = answered === i;
                  let style = { borderColor: '#E0D6CA', color: '#544838', background: 'transparent' };
                  if (result && isCorrect) style = { borderColor: '#9E733C', color: '#262016', background: 'rgba(158,115,60,0.12)' };
                  else if (result && chosen && !isCorrect) style = { borderColor: '#B4543F', color: '#8B3A2A', background: 'rgba(180,84,63,0.08)' };
                  else if (result) style = { borderColor: '#EDE5DB', color: '#B3A68F', background: 'transparent' };
                  return (
                    <button key={i} disabled={answered !== null} onClick={() => answer(i)}
                      className="w-full text-left px-4 sm:px-5 py-3 rounded-xl border font-display text-sm sm:text-base transition-colors hover:border-[#9E733C]"
                      style={style} data-testid={`video-quiz-option-${i}`}>
                      <span className="inline-flex items-center gap-3">
                        <span className="inline-block w-5 h-[3px] shrink-0" style={{ background: result && isCorrect ? '#9E733C' : chosen && result && !isCorrect ? '#B4543F' : '#C9A96E' }} />
                        {opt}
                        {result && isCorrect && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#9E733C' }} />}
                        {chosen && result && !isCorrect && <XCircle className="w-4 h-4 shrink-0" style={{ color: '#B4543F' }} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              {result && (
                <div className="mt-4 sm:mt-6" ref={(el) => el && el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}>
                  {result.correct === false && (
                    <p className="text-xs font-semibold mb-1" style={{ color: '#B4543F' }} data-testid="video-quiz-retry-hint">
                      Nicht ganz — diese Frage kommt später noch einmal.
                    </p>
                  )}
                  <p className="text-sm sm:text-base mb-4" style={{ color: '#544838' }} data-testid="video-quiz-feedback">{result.explanation}</p>
                  <button onClick={resume} className="px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: '#9E733C' }} data-testid="video-quiz-continue-btn">
                    {result.completed ? 'Abschließen' : endMode ? 'Nächste Frage' : 'Weiter im Video'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>, document.body)}
      {done && !quiz && !done.already && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-5" style={{ background: 'rgba(38,32,22,0.92)', animation: 'vqFade 0.45s ease both' }} data-testid="video-quiz-done-overlay">
          <div className="max-w-md w-full rounded-2xl p-6 sm:p-8 text-center" style={{ background: '#F6EFE4' }}>
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#9E733C' }} />
            <p className="font-display text-xl sm:text-2xl mb-2" style={{ color: '#262016' }}>Video-Quiz bestanden!</p>
            {done.tasks_open > 0 ? (
              <div data-testid="video-quiz-tasks-hint">
                <p className="text-sm mb-4" style={{ color: '#544838' }}>
                  <Lock className="w-4 h-4 inline mr-1" style={{ color: '#B4543F' }} />
                  Es {done.tasks_open === 1 ? 'wartet noch 1 offene Aufgabe' : `warten noch ${done.tasks_open} offene Aufgaben/Fallbeispiele`} in diesem Modul.
                  Erst lösen — dann öffnet sich das nächste Modul automatisch.
                </p>
                <button onClick={() => setDone({ already: true })} className="px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: '#9E733C' }} data-testid="video-quiz-close-btn">
                  Zu den Aufgaben
                </button>
              </div>
            ) : done.next_module_id ? (
              <div data-testid="video-quiz-next-module">
                <p className="text-sm mb-4" style={{ color: '#544838' }}>
                  Das nächste Modul ist jetzt freigeschaltet{done.next_module_title ? `: „${done.next_module_title}"` : ''}.
                </p>
                <button onClick={() => navigate(`/module/${done.next_module_id}`)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: '#9E733C' }} data-testid="video-quiz-next-btn">
                  Weiter zum nächsten Modul <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button onClick={() => setDone({ already: true })} className="px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: '#9E733C' }} data-testid="video-quiz-close-btn">
                Fertig
              </button>
            )}
          </div>
        </div>, document.body)}
    </div>
  );
};

export default VideoQuizPlayer;
